import {
  useAutoSignIn,
  useDecodedToken,
  useHasPermission,
  useIsAuthenticated,
  useIsFeatureWhitelisted,
  useIsImpersonationMode,
  useIsNavMenuHidden,
  useOktaContext,
  useRefreshAccessToken,
} from 'mf_core/auth';
import { useInX4AFrame, useSiteConfig } from 'mf_core/config';
import { RenderRemoteDialog } from 'mf_core/dialog';
import { useLogoutContext } from 'mf_core/logout';
import { ScopeProvider } from 'mf_core/provider';
/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
  RouteObject,
  useLocation,
  useNavigate,
  useRoutes,
} from 'react-router-dom';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import { useIsDown, useIsResolution } from '@im/ui-lib/responsive';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { IngramBecomeReseller } from '../common/IngramBecomeReseller';
import { usePartnerWithUs } from '../common/IngramBecomeReseller/usePartnerWithUs';
import { IngramFooter } from '../common/IngramFooter';
import { IngramFooterLite } from '../common/IngramFooter/IngramFooterLite';
import { IngramHeader } from '../common/IngramHeader/IngramHeader';
import IngramHelpMenu from '../common/IngramHelpMenu/IngramHelpMenu';
import { IngramMobile } from '../common/IngramMobile';
import { IngramSideNav } from '../common/IngramSideNav';
import { useFeatureFlagBase } from '../hooks/use-feature-flag-base';
// import { useNotifications } from '../hooks/use-notifications';
import { usePreferredLanguageChange } from '../hooks/use-preferred-language';
import { useXScroll } from '../hooks/use-x-scroll';
import { useAreWidgetsConfigured } from '../hooks/useAreWidgetsConfigured';
import { useCMPCardRedirect } from '../hooks/useCMPCardRedirect';
import { useGetSearchSuggestions } from '../hooks/useGetSearchSuggestions';
import { useImpersonationHeaderProps } from '../hooks/useImpersonationHeaderProps';
import { useTnCRedirect } from '../hooks/useTnCRedirect';
import { AutoLoginImOnlineFrame } from './AutoLoginImOnlineFrame';
import { DebugAccessToken } from './DebugAccessToken';
import { DebugToolbar } from './DebugToolbar';
import { FramedPageTitle } from './FramedPageTitle';
import { InvalidAccessToken } from './InvalidAccessToken';
import { PerformanceErrorAlert } from './PerformanceErrorAlert';
import { useScriptInjectors } from './injectors/injectors';
import { useOneTrust } from './injectors/useOneTrust';
import {
  PROSPECT_RESELLER_PUBLIC_ROUTES,
  PROSPECT_RESELLER_ROUTES,
} from './prospectResellerPaths';
import { useAppRoutes } from './routes/useAppRoutes';
import { useAvatarProps } from './useAvatarProps';
import { useProcessPostMsgs } from './useProcessPostMsgs';

const win = window as any;
const HEADER_HEIGHT = 52;
const LEFT_NAV_WIDTH = 64;
const HIDE_SEARCHBAR_PRODUCT_VENDORS_COUNTRIES: Array<string> = ['br'];
win.LAYOUT_SPECS = {
  HEADER_HEIGHT: HEADER_HEIGHT,
  LEFT_NAV_WIDTH: LEFT_NAV_WIDTH,
};

let flagTest = false;

export function App() {
  const checkURL = localStorage.getItem('conurl') || '';
  if (!flagTest && checkURL === '') {
    localStorage.setItem('conurl', window.location.search);
    flagTest = true;
  }
  useOneTrust();
  const isAuthenticated = useIsAuthenticated();
  const areWidgetsConfigured = useAreWidgetsConfigured();

  const decodedToken = useDecodedToken();
  const isImpersonationMode = useIsImpersonationMode();
  const isNavMenuHidden = useIsNavMenuHidden();
  const showDebugToolBar = false;
  const resellerProspect = decodedToken?.resellerProspect || false;
  const appRoutes = useAppRoutes();
  const { idpLogin, siteCode } = useSiteConfig();

  useProcessPostMsgs();
  useCMPCardRedirect();
  useTnCRedirect({ isAuthenticated, isImpersonationMode, resellerProspect });
  usePreferredLanguageChange(isAuthenticated);

  const hasPermission = useHasPermission();
  const logoutContext = useLogoutContext();
  const [isLogoutEnabled, setIsLogoutEnabled] = useState(false);
  const [isTokenRefreshed, setTokenRefreshed] = useState(false);
  const [loaderText, setLoadingText] = useState('Common.LoaderText');

  useEffect(() => {
    if (logoutContext) {
      setIsLogoutEnabled(
        !logoutContext.isLoading && !!logoutContext.data?.isLogoutEnabled
      );
    }
  }, [logoutContext]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isTempDrawer = useIsDown('lg', true);
  useEffect(() => {
    setDrawerOpen(false);
  }, [isTempDrawer]);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isLogin = pathname === '/login' || pathname === '/campaign/login';
  const isForgotPassword = pathname === '/account/forgotpassword';
  const isResellerRegistration =
    pathname.toLocaleLowerCase() === '/reselleronboarding/registration';
  const isResellerDetailsV2 =
    pathname.toLocaleLowerCase() === '/reselleronboarding/resellerdetails-v2';
  const isDocusignRedirectIframe =
    pathname.toLocaleLowerCase() === '/reselleronboarding/docusign-redirect';
  const isNotAppstorePath = !pathname.includes('integrationshub');
  const isFrame = pathname === '/frame';
  const isPublicFrame = pathname === '/frame-public';
  const isCart = pathname === '/my/cart';
  const isCheckout =
    pathname.startsWith('/my/checkout') ||
    pathname === '/my/vendorRequestedInformation';
  const isQuoteItems = pathname === '/my/quotemgmt/quoteitems';
  const isDashboard = pathname === '/my/dashboard';
  const isInsightsV2 = pathname === '/insight/insights-v2';
  const isOrderConfirmation = pathname === '/my/orderConfirmation';
  const isCustomizationView = pathname === '/my/dashboardcustomization';
  const isPayment = /my\/payment\/.*-hh/.test(pathname);
  const isPaymentVerification = pathname.includes(
    '/my/checkout/verification-hh'
  );
  const isResellerProspectPublicRoute =
    PROSPECT_RESELLER_PUBLIC_ROUTES.includes(pathname?.toLowerCase());
  const showCartIconOnHeader =
    isAuthenticated && hasPermission(['CreateBaskets']);
  const { boValues, isReady } = useFeatureFlagBase();
  const enableAISuggestions = isReady
    ? boValues['Enable_AISuggestions'] === 'true'
    : false;
  const isCMPProductExcludedReseller = isReady
    ? boValues['CMP_Products_ExcludedResellers'] === 'true'
    : false;
  const useUrlEncoding = isReady
    ? boValues['X4C_Url_Encoding_Feature'] === 'true'
    : false;

  useEffect(() => {
    if (
      resellerProspect &&
      !PROSPECT_RESELLER_ROUTES.includes(pathname?.toLowerCase()) &&
      pathname !== '/logout'
    ) {
      navigate('/reselleronboarding/resellerdetails');
    }
  }, [pathname, resellerProspect, navigate]);

  useEffect(() => {
    localStorage.setItem('useUrlEncoding', useUrlEncoding ? 'true' : 'false');
  }, [useUrlEncoding]);

  const isMobile = useIsResolution({ max: 768 });
  const [isFirstLogin, setIsFirstLogin] = useState(true);
  const [isCreateDefaultBasket, setIsCreateDefaultBasket] = useState(false);

  useEffect(() => {
    setIsFirstLogin(
      pathname === '/welcome-video' || pathname === '/terms-and-conditions'
    );
  }, [pathname]);

  useEffect(() => {
    if (isAuthenticated && !isCreateDefaultBasket && !resellerProspect) {
      setIsCreateDefaultBasket(true);
    }
  }, [isAuthenticated, isCreateDefaultBasket, resellerProspect]);

  useScriptInjectors({ isFirstLogin, resellerProspect });

  const { isInX4AFrame, xvFrameConfig } = useInX4AFrame();

  const hideSideNav =
    (!isAuthenticated && !isLogoutEnabled) ||
    isLogin ||
    isForgotPassword ||
    isFirstLogin ||
    isOrderConfirmation ||
    isPaymentVerification ||
    isPayment ||
    (isCustomizationView && !areWidgetsConfigured) ||
    (isImpersonationMode && isNavMenuHidden && !isInX4AFrame) || //CTO app
    (isInX4AFrame && xvFrameConfig.hideSideNav) || //framed into X4A
    Boolean(resellerProspect) ||
    isResellerProspectPublicRoute ||
    isDocusignRedirectIframe;

  const hideHeader = Boolean(
    pathname
      .split('/')
      .find(
        (segment) =>
          segment.endsWith('-hh') ||
          segment.toLowerCase() === 'login' ||
          segment.toLowerCase() === 'forgotpassword'
      ) ||
      isFirstLogin ||
      xvFrameConfig.hideHeader ||
      isResellerRegistration ||
      isDocusignRedirectIframe
  );

  const hideFooter =
    isLogin ||
    isCheckout ||
    isFirstLogin ||
    isPayment ||
    isPaymentVerification ||
    isPublicFrame ||
    isResellerDetailsV2 ||
    isDocusignRedirectIframe ||
    (isImpersonationMode && isNavMenuHidden && xvFrameConfig.hideSideNav);

  const isFeatureWhitelisted = useIsFeatureWhitelisted();
  const isCmpWhitelisted = isFeatureWhitelisted('CMP');

  const getSearchSuggestions = useGetSearchSuggestions(
    isCmpWhitelisted,
    enableAISuggestions,
    isMobile,
    isCMPProductExcludedReseller
  );

  const avatarProps = useAvatarProps();
  const headerHeight = useMemo(() => {
    let headerHeight = HEADER_HEIGHT;
    if (hideHeader) headerHeight = 0;
    else if (showDebugToolBar) headerHeight = HEADER_HEIGHT + 40;
    win.LAYOUT_SPECS.HEADER_HEIGHT = headerHeight;
    return headerHeight;
  }, [hideHeader, showDebugToolBar]);

  const leftNavWidth = useMemo(() => {
    let leftNavWidth = LEFT_NAV_WIDTH;
    if (hideSideNav || isTempDrawer) leftNavWidth = 0;
    win.LAYOUT_SPECS.LEFT_NAV_WIDTH = leftNavWidth;
    return leftNavWidth;
  }, [hideSideNav, isTempDrawer]);

  type UserActionType = 'favorite' | 'notification' | 'cart' | 'view-quote';
  const onUserActionClick = (action: UserActionType) => {
    switch (action) {
      case 'favorite':
        navigate('product/favoriteproducts');
        break;
      case 'cart':
        navigate('my/cart');
    }
  };

  useEffect(() => {
    if (!isDashboard || !isCmpWhitelisted) return;
    setLoadingText('Common.LoaderText');
    const loaderTextTimeout = setTimeout(function () {
      setLoadingText('HomePage.LoaderText');
    }, 5000);
    return () => {
      clearTimeout(loaderTextTimeout);
    };
  }, [isCmpWhitelisted, isDashboard]);

  const centerLoaderText =
    isAuthenticated && isDashboard ? loaderText : undefined;

  const disableHeader = isCustomizationView && !areWidgetsConfigured;

  // Refresh access token if a user is already logged in (needed for impersonation mode)
  // Try to auto-login if a user is not logged in
  const { authState } = useOktaContext();
  const refreshAccessToken = useRefreshAccessToken();
  const autoSignIn = useAutoSignIn();

  useEffect(() => {
    if (authState && !isTokenRefreshed) {
      setTokenRefreshed(true);
      if (authState.isAuthenticated && !pathname.includes('/login/callback')) {
        refreshAccessToken();
      } else if (
        !idpLogin &&
        !pathname.includes('/login') &&
        !pathname.includes('/login/callback') &&
        !pathname.includes('/productsearch') &&
        !pathname.includes('/productdetails')
      ) {
        autoSignIn();
      }
    }
  }, [
    authState,
    idpLogin,
    isTokenRefreshed,
    refreshAccessToken,
    autoSignIn,
    pathname,
  ]);

  const impersonationHeaderProps = useImpersonationHeaderProps();

  const hideSearchBarProductVendorNav =
    HIDE_SEARCHBAR_PRODUCT_VENDORS_COUNTRIES.includes(siteCode) &&
    !isAuthenticated;

  const enableSearchOnQuote = isReady
    ? boValues['QM_EnableSearch_BO'] === 'true'
    : false;
  const hideSearchBar =
    hideSearchBarProductVendorNav ||
    isCheckout ||
    isFirstLogin ||
    (isImpersonationMode && isNavMenuHidden) ||
    (isQuoteItems && isReady && enableSearchOnQuote);

  const enableXScroll = useXScroll();

  // const { isNotificationEnabled, notificationDropProps } = useNotifications(
  //   !isCheckout && !isImpersonationMode
  // );
  const partnerWithUsData = usePartnerWithUs(!isAuthenticated);

  const trimAvatorPropsForResellerProspect = (avatarData: any) => {
    const { navItems, ...rest } = avatarData;
    return { ...rest, resellerProspect: true };
  };

  if (!appRoutes) {
    return <CenterLoader loaderText={centerLoaderText} />;
  }

  if (
    isMobile &&
    (isAuthenticated ||
      pathname === '/login' ||
      pathname === '/product/productdetails')
  ) {
    return <IngramMobile />;
  }

  return (
    <>
      <DebugAccessToken />
      <Box height="100vh" position="relative" role="main" width="100vw">
        {!hideHeader && (
          <div
            data-testid="HeaderSection"
            style={
              disableHeader
                ? { opacity: '.6', pointerEvents: 'none' }
                : undefined
            }
          >
            {showDebugToolBar && <DebugToolbar />}
            <IngramHeader
              AvatarProps={
                resellerProspect
                  ? trimAvatorPropsForResellerProspect(avatarProps)
                  : avatarProps
              }
              enableAISuggestions={enableAISuggestions}
              getSearchSuggestions={getSearchSuggestions}
              impersonationHeaderProps={impersonationHeaderProps}
              isAuthenticated={Boolean(
                (authState !== null
                  ? authState.isAuthenticated
                  : isAuthenticated) && !isFirstLogin
              )}
              isCmpWhitelisted={isCmpWhitelisted}
              isLogoClickable={!(isImpersonationMode && isNavMenuHidden)}
              isMobile={isMobile}
              // isNotificationEnabled={isNotificationEnabled}
              // notificationDropProps={notificationDropProps}
              onMenuButtonClick={() => setDrawerOpen(!drawerOpen)}
              onUserActionClick={onUserActionClick}
              showAvatar={
                !isCheckout && !(isImpersonationMode && isNavMenuHidden)
              }
              showCartIcon={
                showCartIconOnHeader &&
                !isFirstLogin &&
                !isCheckout &&
                !(isImpersonationMode && isNavMenuHidden)
              }
              showCulturePicker={!isCheckout}
              showFavoritesIcon={
                !isCheckout &&
                !(isImpersonationMode && isNavMenuHidden) &&
                !resellerProspect
              }
              showImpersonationHeader={isImpersonationMode}
              showSeachBar={
                !hideSearchBar &&
                !resellerProspect &&
                !isResellerProspectPublicRoute &&
                isNotAppstorePath
              }
            />
          </div>
        )}
        <Box
          sx={{
            bottom: 0,
            left: isTempDrawer || hideSideNav ? 0 : leftNavWidth,
            position: 'absolute',
            right: 0,
            top: headerHeight,
          }}
        >
          <ScopeProvider scope={pathname}>
            {isFrame ? (
              <Suspense fallback={<CenterLoader />}>
                <FramedPageTitle url={search} />
                <RenderContent appRoutes={appRoutes} />
              </Suspense>
            ) : (
              <Box
                id="main-view"
                sx={{
                  backgroundColor:
                    (isCmpWhitelisted && isDashboard) || isInsightsV2
                      ? '#F6F7FB'
                      : 'transparent',
                  height: '100%',
                  overflowX: enableXScroll ? 'auto' : 'hidden',
                  overflowY: 'auto',
                  width: '100%',
                }}
              >
                <PerformanceErrorAlert />
                <Box
                  sx={{
                    minWidth: enableXScroll ? '1024px' : 'unset',
                  }}
                >
                  <Root>
                    <ScopeProvider scope={pathname}>
                      <Suspense
                        fallback={
                          <CenterLoader loaderText={centerLoaderText} />
                        }
                      >
                        <RenderContent appRoutes={appRoutes} />
                      </Suspense>
                    </ScopeProvider>
                  </Root>
                  {(!hideFooter &&
                    (isCart ? (
                      <IngramFooterLite />
                    ) : (
                      <IngramFooter
                        footerAction={partnerWithUsData}
                        isAuthenticated={isAuthenticated}
                        isMobile={isMobile}
                      />
                    ))) ||
                    null}
                </Box>
              </Box>
            )}
            <InvalidAccessToken />
          </ScopeProvider>
        </Box>
        {isReady && boValues['ACC_MGMT_EnableX4CReactUI'] ? (
          <></>
        ) : (
          <AutoLoginImOnlineFrame />
        )}
        {/* <AutoLoginImOnlineFrame /> */}
        <RenderRemoteDialog />

        {/* Drawer at the end so its menus are above the View */}
        {!hideSideNav && (
          <IngramSideNav
            drawerOpen={drawerOpen}
            headerHeight={headerHeight}
            hoverRequired={true}
            isAuthenticated={isAuthenticated}
            isCheckout={isCheckout}
            isMobile={isMobile}
            isTempDrawer={isTempDrawer}
            setDrawerOpen={setDrawerOpen}
          />
        )}
        {isMobile && !isAuthenticated
          ? null
          : !hideSideNav && !resellerProspect && <IngramHelpMenu />}
        {isLogoutEnabled && <IngramBecomeReseller data={partnerWithUsData} />}
      </Box>
    </>
  );
}

function RenderContent({ appRoutes }: { appRoutes: RouteObject[] }) {
  const content = useRoutes(appRoutes);
  return content;
}

const Root = styled('div')(() => ({
  margin: 'auto',
  maxWidth: '1920px',
  minHeight: `calc(100vh - ${win.LAYOUT_SPECS.HEADER_HEIGHT}px)`,
}));

declare global {
  interface Window {
    OOo: any;
  }
}
